import { Head, Link, router } from "@inertiajs/react"
import { CheckCircle2, Pencil, Receipt, Trash2 } from "lucide-react"
import { useState } from "react"

import { BillingTab } from "@/pages/Projects/tabs/BillingTab"
import { CrewTab } from "@/pages/Projects/tabs/CrewTab"
import { EditProjectDialog } from "@/components/edit-project-dialog"
import { ExpensesTab } from "@/pages/Projects/tabs/ExpensesTab"
import { Kpi, KpiStrip, StatusBadge } from "@/components/steel"
import { OverviewTab } from "@/pages/Projects/tabs/OverviewTab"
import { ProductionTab } from "@/pages/Projects/tabs/ProductionTab"
import { Button } from "@/components/ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { formatCurrency, shortMoney } from "@/lib/utils"
import type { ProjectShowProps } from "@/pages/Projects/tabs/types"

function formatDate(value?: string | null): string {
  if (!value) return "—"
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

export default function ProjectShow(props: ProjectShowProps) {
  const { project, billing, production, team, expenses, burndown, expenseBreakdown, invoices, milestones } = props
  const [editOpen, setEditOpen] = useState(false)

  const used = project.initialBudget > 0 ? (project.currentSpend / project.initialBudget) * 100 : 0
  const totalSqft =
    (production.steelSqftTotal ?? 0) + (production.wallSqftTotal ?? 0) + (production.roofSqftTotal ?? 0)
  const doneSqft =
    (production.steelSqftCompleted ?? 0) + (production.wallSqftCompleted ?? 0) + (production.roofSqftCompleted ?? 0)
  const completePct = totalSqft > 0 ? (doneSqft / totalSqft) * 100 : 0

  return (
    <>
      <Head title={project.name} />

      <Tabs defaultValue="overview">
        <div className="border-b border-border px-6 pt-4">
          <div className="flex items-start justify-between gap-6">
            <div className="min-w-0">
              <div className="mb-1.5 flex items-center gap-2.5">
                <span className="num text-meta tracking-[0.06em] text-muted-foreground">{project.id}</span>
                <StatusBadge status={project.status} />
                <span className="text-label text-muted-foreground">
                  {formatDate(project.startDate)} – {formatDate(project.endDate)}
                </span>
              </div>
              <h1 className="mb-1 text-[22px] font-semibold leading-tight tracking-[-0.025em]">
                {project.name}
              </h1>
              <p className="text-body text-muted-foreground">
                {project.client} · {project.location}
              </p>
            </div>

            <div className="shrink-0 text-right">
              <p className="eyebrow mb-1">Contract</p>
              <p className="num text-[24px] font-semibold tracking-[-0.02em] text-whisper">
                {formatCurrency(project.totalRevenue)}
              </p>
              <div className="mt-2 flex justify-end gap-1.5">
                <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                  <Pencil />
                  Edit
                </Button>
                <Button size="sm" asChild>
                  <Link href={`/projects/${project.id}/invoices/new`}>
                    <Receipt />
                    Invoice
                  </Link>
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-3 pb-2.5">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="production">Production</TabsTrigger>
              <TabsTrigger value="expenses">Expenses</TabsTrigger>
              <TabsTrigger value="crew">Crew</TabsTrigger>
              <TabsTrigger value="billing">Billing</TabsTrigger>
            </TabsList>
          </div>
        </div>

        <KpiStrip>
          <Kpi
            compact
            label="Budget"
            value={shortMoney(project.initialBudget)}
            hint={`${Math.round(used)}% committed`}
          />
          <Kpi
            compact
            label="Cost to date"
            value={shortMoney(project.currentSpend)}
            hint={`${shortMoney(project.initialBudget - project.currentSpend)} left`}
          />
          <Kpi
            compact
            label="Planned profit"
            value={production.plannedProfit != null ? shortMoney(production.plannedProfit) : "—"}
            hint="From the bid"
          />
          <Kpi
            compact
            label="Projected profit"
            value={production.projectedProfit != null ? shortMoney(production.projectedProfit) : "—"}
            hint={
              production.projectedProfitLow != null && production.projectedProfitHigh != null
                ? `${shortMoney(production.projectedProfitLow)} – ${shortMoney(production.projectedProfitHigh)} (±1σ)`
                : "Enter production data"
            }
          />
          <Kpi
            compact
            label="Complete"
            value={totalSqft > 0 ? `${completePct.toFixed(1)}%` : "—"}
            hint={
              totalSqft > 0
                ? `${doneSqft.toLocaleString()} of ${totalSqft.toLocaleString()} sq ft`
                : "No production data"
            }
          />
        </KpiStrip>

        <TabsContent value="overview">
          <OverviewTab
            burndown={burndown}
            budget={project.initialBudget}
            expenseBreakdown={expenseBreakdown}
            invoices={invoices}
            milestones={milestones}
            projectId={project.id}
            retrospective={project.retrospective}
            spend={project.currentSpend}
          />
        </TabsContent>

        <TabsContent value="production">
          <ProductionTab projectId={project.id} production={production} endDate={project.endDate} />
        </TabsContent>

        <TabsContent value="expenses">
          <ExpensesTab projectId={project.id} expenses={expenses} initialBudget={project.initialBudget} />
        </TabsContent>

        <TabsContent value="crew">
          <CrewTab
            projectId={project.id}
            team={team}
            unassignedEmployees={props.unassignedEmployees}
          />
        </TabsContent>

        <TabsContent value="billing">
          <BillingTab billing={billing} invoices={invoices} projectId={project.id} />
        </TabsContent>
      </Tabs>

      <div className="flex flex-wrap items-center gap-2 border-t border-border px-6 py-4">
        {project.isCompleted ? (
          <StatusBadge
            status="completed"
            label={
              project.actualFinishDate ? `Completed ${formatDate(project.actualFinishDate)}` : "Completed"
            }
          />
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={() =>
              router.patch(
                `/projects/${project.id}`,
                { status: "completed", actualFinishDate: project.endDate },
                { preserveScroll: true },
              )
            }
          >
            <CheckCircle2 />
            Mark complete
          </Button>
        )}
        <Button
          variant="outline"
          size="sm"
          className="text-destructive"
          onClick={() => {
            if (window.confirm(`Delete "${project.name}"? Expenses, assignments and time entries go with it.`)) {
              router.delete(`/projects/${project.id}`)
            }
          }}
        >
          <Trash2 />
          Delete project
        </Button>
      </div>

      <EditProjectDialog
        key={`${project.name}-${project.initialBudget}-${project.totalRevenue}`}
        open={editOpen}
        onOpenChange={setEditOpen}
        project={project}
      />
    </>
  )
}
